<template>
    <div>
        <div class="d-flex justify-content-between">
            <h1 class="page-header">{{ $t('signature.pageTitle') }}</h1>
            <ts-breadcrumb :items="[
                { text: $t('toHomePage'), href: '/' },
                { text: $t('signature.pageTitle'), active: true }
            ]" />
        </div>

        <ts-card>
            <ts-card-wrapper>
                <div class="row">
                    <div class="col-sm-12 col-md-6 col-lg-9 ts-space-x-2">
                        <ts-button color="info" outline @click.prevent="show_form = true">
                            {{ $t('add_new') }}
                        </ts-button>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3 d-flex justify-content-end">
                        <ts-input v-model="search" :placeholder="$t('search')" class="mr-2" />
                    </div>
                </div>
            </ts-card-wrapper>

            <div class="overflow-auto">
                <ts-table :columns="columns" :loading="loading" :records="resources">
                    <template v-slot="{ record }">
                        <td>
                            <div v-if="record.signatures">
                                <img :src="record.signatures" alt="Signature"
                                    style="max-width: 120px; max-height: 60px;  cursor: pointer;" />
                            </div>
                            <div v-else>-</div>
                        </td>

                        <td>
                            {{ record.main_program ? record.main_program.acp_name_foreign : '-' }}
                        </td>
                        <td>
                            <span v-if="record.sub_programs" v-for="(item, index) in record.sub_programs" :key="index">
                                {{ index === 0 ? item.acps_name_foreign : ', ' + item.acps_name_foreign }}
                            </span>
                            <span v-else>N/A</span>
                        </td>
                        <td class="text-center">{{ record.signature_type }}</td>
                        <td>{{ record.position_kh }}</td>
                        <td>{{ record.position }}</td>
                        <td>{{ record.signed_by_kh }}</td>
                        <td>{{ record.signed_by }}</td>
                        <td class="text-center">
                            <a href="#" v-tooltip="$t('edit')" class="mr-2 text-primary" @click.prevent="edit(record)">
                                <i class="fas fa-edit"></i>
                            </a>
                        </td>
                    </template>
                </ts-table>
            </div>

            <div class="d-flex justify-content-end p-15">
                <ts-pagination v-model="pagination" @navigate="fetchData" />
            </div>

            <a-modal v-if="show_form" v-model="show_form" :title="$t('signature.pageTitle')" :zIndex="1020"
                @cancel="clearInput" :footer="null" :maskClosable="false" width="900px">
                <form-component v-model="model" :validate="errors" :preview-image="priview"
                    :sub-program-options="sub_programs" :loading-sub="fetch_sub_program"
                    @update-sub-programs="handleSubProgramChange">
                    <template v-slot:action-button>
                        <ts-button class="mr-2" @click.prevent="cancelCreate">
                            {{ $t('cancel') }}
                        </ts-button>
                        <ts-button v-if="model.id == 0" class="mr-2" color="primary" outline :waiting="waiting_new"
                            @click.prevent="saveNew">{{ $t('save_add_new') }}</ts-button>
                        <ts-button v-if="model.id == 0" color="primary" :waiting="waiting" @click.prevent="save">
                            {{ $t('save') }}
                        </ts-button>
                        <ts-button v-if="model.id != 0" color="primary" :waiting="waiting" @click.prevent="onUpdate">
                            {{ $t('update') }}
                        </ts-button>
                    </template>
                </form-component>
            </a-modal>
        </ts-card>
    </div>
</template>

<script>
import FormComponent from './form'
import { mapState, mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import { debounce } from 'lodash'

export default {
    name: 'signature',
    components: { FormComponent },
    data() {
        return {
            loading: false,
            show_form: false,
            waiting: false,
            waiting_new: false,
            errors: new Errors(),
            priview: '',
            sub_programs: [],
            fetch_sub_program: false,
            imagePreview: {
                visible: false,
                url: ''
            },
            model: {
                id: 0,
                acp_id: null,
                acps_id: [],
                from_branch: '',
                signature_type: 'Card',
                position: '',
                signed_by: '',
                signatures: '',
                file: null,
                position_kh: '',
                signed_by_kh: '',
            }
        }
    },
    computed: {
        ...mapState('signature', ['resources', 'pagination']),
        columns() {
            return [
                { name: this.$t('signature.signature') },
                { name: this.$t('signature.mainProgram') },
                { name: this.$t('signature.subProgram') },
                {
                    name: this.$t('signature.signatureType'),
                    style: 'text-align:center'
                },
                { name: this.$t('signature.positionKh') },
                { name: this.$t('signature.position') },
                { name: this.$t('signature.signedKh') },
                { name: this.$t('signature.signBy') },
                {
                    name: this.$t('inventory.operation.stock_count_entry.actions'),
                    style: 'text-align:center; width:7%'
                }
            ]
        },
        search: {
            get() {
                return this.$store.state.signature.search
            },
            set(newValue) {
                this.$store.commit('signature/SET_SEARCH', newValue)
                this.$store.commit('signature/RESET_CURRENT_PAGE')
            }
        },
    },
    created() {
        this.fetchData()
    },
    methods: {
        ...mapActions('signature', ['getMainProgram', 'getSubProgram']),

        fetchData(attributes) {
            this.loading = true
            this.$store
                .dispatch('signature/fetch', { ...attributes })
                .then(() => (this.loading = false))
                .catch(error => {
                    this.loading = false
                    this.$toasted.error(error.message)
                })
        },

        handleSubProgramChange(subPrograms) {
            this.sub_programs = subPrograms
        },

        cancelCreate() {
            this.show_form = false
            this.clearInput()
        },

        save() {
            this.errors = new Errors()
            this.waiting = true

            let formData = new FormData()
            formData.append('from_branch', this.model.from_branch || '')
            formData.append('position', this.model.position)
            formData.append('signed_by', this.model.signed_by)
            formData.append('position_kh', this.model.position_kh || '')
            formData.append('signed_by_kh', this.model.signed_by_kh || '')
            formData.append('signature_type', this.model.signature_type)
            formData.append('acps_id', JSON.stringify(this.model.acps_id))
            formData.append('acp_id', this.model.acp_id || '')

            if (this.model.file) {
                formData.append('file', this.model.file)
            }

            this.$store
                .dispatch('signature/store', formData)
                .then((response) => {
                    this.$toasted.success(response.message)
                    this.errors = new Errors()
                    this.fetchData()
                    this.show_form = false
                    this.clearInput()
                })
                .catch((error) => {
                    this.$toasted.error(error.message)
                    this.errors = new Errors(error.errors)
                })
                .finally(() => {
                    this.waiting = false
                })
        },

        saveNew() {
            this.errors = new Errors()
            this.waiting_new = true

            let formData = new FormData()
            formData.append('from_branch', this.model.from_branch || '')
            formData.append('position', this.model.position)
            formData.append('signed_by', this.model.signed_by)
            formData.append('position_kh', this.model.position_kh || '')
            formData.append('signed_by_kh', this.model.signed_by_kh || '')
            formData.append('signature_type', this.model.signature_type)
            formData.append('acps_id', JSON.stringify(this.model.acps_id))
            formData.append('acp_id', this.model.acp_id || '')

            if (this.model.file) {
                formData.append('file', this.model.file)
            }

            this.$store
                .dispatch('signature/store', formData)
                .then((response) => {
                    this.$toasted.success(response.message)
                    this.errors = new Errors()
                    this.clearInput()
                    this.fetchData()
                })
                .catch((error) => {
                    this.$toasted.error(error.message)
                    this.errors = new Errors(error.errors)
                })
                .finally(() => {
                    this.waiting_new = false
                })
        },

        async edit(record) {
            this.show_form = true
            await this.$nextTick()

            this.model.id = record.id
            this.model.from_branch = record.from_branch || ''
            this.model.position = record.position || ''
            this.model.position_kh = record.position_kh || ''
            this.model.signed_by = record.signed_by || ''
            this.model.signed_by_kh = record.signed_by_kh || ''
            this.model.signature_type = record.signature_type || 'Card'
            this.model.file = null
            this.model.signatures = record.signatures || ''
            this.priview = record.signatures || 'https://cdn.pixabay.com/photo/2012/04/11/11/16/signature-27485_960_720.png'

            let subProgramIds = []
            if (record.sub_programs && Array.isArray(record.sub_programs) && record.sub_programs.length > 0) {
                subProgramIds = record.sub_programs.map(sp => sp.acps_id)
            } else if (record.acps_id !== null && record.acps_id !== undefined && record.acps_id !== '') {
                if (Array.isArray(record.acps_id)) {
                    subProgramIds = record.acps_id
                } else if (typeof record.acps_id === 'number') {
                    subProgramIds = [record.acps_id]
                } else if (typeof record.acps_id === 'string') {
                    const trimmed = record.acps_id.trim()
                    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                        try {
                            subProgramIds = JSON.parse(trimmed)
                        } catch (e) {
                            console.error('Error parsing JSON array:', e)
                            subProgramIds = []
                        }
                    } else if (trimmed.includes(',')) {
                        subProgramIds = trimmed.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
                    } else {
                        const num = parseInt(trimmed)
                        if (!isNaN(num)) {
                            subProgramIds = [num]
                        } else {
                            subProgramIds = []
                        }
                    }
                }
            }

            // Handle main program and subprogram
            if (record.acp_id) {
                this.model.acp_id = record.acp_id
                this.fetch_sub_program = true

                try {
                    const response = await this.getSubProgram({
                        filter: {
                            acp_id: record.acp_id
                        }
                    })

                    this.sub_programs = response.data.map(el => ({
                        value: el.acps_id,
                        label: el.acps_name_foreign,
                    }))

                    await this.$nextTick()

                    setTimeout(() => {
                        this.model.acps_id = [...subProgramIds]
                        this.$forceUpdate()
                    }, 150)

                } catch (error) {
                    console.error('Error loading sub programs:', error)
                    this.$toasted.error(error.message)
                    this.sub_programs = []
                } finally {
                    this.fetch_sub_program = false
                }
            } else {
                this.model.acp_id = null
                this.model.acps_id = []
                this.sub_programs = []
            }
        },

        onUpdate() {
            this.errors = new Errors()
            this.waiting = true

            let formData = new FormData()
            formData.append('from_branch', this.model.from_branch || '')
            formData.append('position', this.model.position)
            formData.append('signed_by', this.model.signed_by)
            formData.append('position_kh', this.model.position_kh || '')
            formData.append('signed_by_kh', this.model.signed_by_kh || '')
            formData.append('signature_type', this.model.signature_type)
            formData.append('acps_id', JSON.stringify(this.model.acps_id))
            formData.append('acp_id', this.model.acp_id || '')
            if (this.model.file) {
                formData.append('file', this.model.file)
            } else if (this.model.signatures) {
                formData.append('signatures', this.model.signatures)
            }
            for (let pair of formData.entries()) {
                console.log(pair[0], pair[1])
            }

            this.$store
                .dispatch('signature/update', {
                    id: this.model.id,
                    data: formData
                })
                .then(response => {
                    this.$toasted.success(response.message)
                    this.fetchData()
                    this.show_form = false
                    this.clearInput()
                })
                .catch(error => {
                    this.$toasted.error(error.message)
                    this.errors = new Errors(error.errors)
                })
                .finally(() => {
                    this.waiting = false
                })
        },

        clearInput() {
            this.model.id = 0
            this.model.position = ''
            this.model.signed_by = ''
            this.model.signatures = ''
            this.model.position_kh = ''
            this.model.signed_by_kh = ''
            this.model.from_branch = ''
            this.model.signature_type = 'Card'
            this.model.acp_id = null
            this.model.acps_id = []
            this.model.file = null
            this.priview = ''
            this.sub_programs = []
            this.errors = new Errors()
        },
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({ search })
        }, 500),
    }
}
</script>